import { getServerTimestampFromPayload } from './authTokens';
import {
  AUTH_REFRESH_COOLDOWN_MS,
  HTTP_STATUS_BANNED,
  HTTP_STATUS_UNAUTHORIZED,
} from './authConstants';
import { isAccessTokenExpiredOrNearExpiry } from './accessTokenExpiry';
import {
  getClockDeltaSeconds,
  updateClockDeltaFromServer,
} from './clockDelta';
import { clearJwtSessionInBrowser } from './clearJwtSessionInBrowser';
import {
  getAccessTokenFromBrowserCookie,
  needsJwtHydrationFromSessionInBrowser,
} from './jwtCookieHelpers';
import { isJwtAuthEnabledInBrowser } from './jwtRollout';

/**
 * Состояние восстановления JWT (single-flight + cooldown).
 */
const recoveryState = {
  inFlight: null,
  lastDoneAt: 0,
  lastSucceeded: null,
};

function markRecoveryDone(succeeded) {
  recoveryState.lastDoneAt = Date.now();
  recoveryState.lastSucceeded = succeeded;
}

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch (e) {
    return null;
  }
}

function syncClockFromResponseBody(body) {
  const serverTimestamp = getServerTimestampFromPayload(body);
  if (serverTimestamp != null) {
    updateClockDeltaFromServer(serverTimestamp);
  }
}

async function requestObtainTokens(options) {
  const logoutOnInvalid = options && options.logoutOnInvalid === true;
  let res;

  try {
    res = await fetch('/api/obtain-tokens/', {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch (e) {
    markRecoveryDone(false);
    return 'failed';
  }

  if (res.status === HTTP_STATUS_BANNED) {
    markRecoveryDone(false);
    return 'banned';
  }

  if (res.ok) {
    syncClockFromResponseBody(await parseJsonSafe(res));
    markRecoveryDone(true);
    return 'ok';
  }

  if (res.status === HTTP_STATUS_UNAUTHORIZED && logoutOnInvalid) {
    await clearJwtSessionInBrowser({ redirectToSignIn: true });
  }

  markRecoveryDone(false);
  return 'failed';
}

async function runRecoveryPipeline() {
  let res;

  try {
    res = await fetch('/api/refresh-tokens/', {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch (e) {
    markRecoveryDone(false);
    return 'failed';
  }

  if (res.status === HTTP_STATUS_BANNED) {
    markRecoveryDone(false);
    return 'banned';
  }

  if (res.ok) {
    syncClockFromResponseBody(await parseJsonSafe(res));
    markRecoveryDone(true);
    return 'ok';
  }

  if (res.status === HTTP_STATUS_UNAUTHORIZED) {
    return requestObtainTokens({ logoutOnInvalid: true });
  }

  markRecoveryDone(false);
  return 'failed';
}

function runWithRecoveryLock(runner) {
  if (typeof window === 'undefined') {
    return Promise.resolve('failed');
  }

  const withinCooldown =
    Date.now() - recoveryState.lastDoneAt < AUTH_REFRESH_COOLDOWN_MS;
  if (withinCooldown && recoveryState.lastSucceeded === true) {
    return Promise.resolve('ok');
  }

  if (recoveryState.inFlight) {
    return recoveryState.inFlight;
  }

  const run = runner()
    .catch(() => 'failed')
    .finally(() => {
      recoveryState.inFlight = null;
    });

  recoveryState.inFlight = run;
  return run;
}

/**
 * TRANSITION: есть сессия без JWT — сначала refresh, при 401 — obtain-tokens.
 */
export async function ensureJwtCookiesWhenSessionPresent() {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isJwtAuthEnabledInBrowser()) {
    return;
  }

  if (!needsJwtHydrationFromSessionInBrowser()) {
    return;
  }

  await runWithRecoveryLock(runRecoveryPipeline);
}

/** Единая точка восстановления JWT: refresh → obtain-tokens → logout. */
export async function recoverFromUnauthorized() {
  if (!isJwtAuthEnabledInBrowser()) {
    return 'failed';
  }

  return runWithRecoveryLock(runRecoveryPipeline);
}

/**
 * Перед запросом: если access нет или скоро истечёт — refresh, затем obtain.
 * TRANSITION: obtain-tokens временный fallback, удалить после перехода на JWT.
 */
export async function ensureFreshAccessTokenBestEffort() {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isJwtAuthEnabledInBrowser()) {
    return;
  }

  await ensureJwtCookiesWhenSessionPresent();

  const token = getAccessTokenFromBrowserCookie();
  if (!isAccessTokenExpiredOrNearExpiry(token, getClockDeltaSeconds())) {
    return;
  }

  await recoverFromUnauthorized();
}
